import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { CheckCircle, Clock, RefreshCw, Play, Loader2, PauseCircle, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

export function CurrentSupportSubscription({ subscription, onManage, onResume, isResuming }) {
    const { t } = useTranslation();

    if (!subscription) return null;

    const usedHours = subscription.totalHours - subscription.remainingHours;
    const usagePercent = Math.min(100, (usedHours / subscription.totalHours) * 100);
    const isPaused = subscription.status === "PAUSED";
    const isCanceled = subscription.status === "CANCELED" || subscription.cancelAtPeriodEnd;
    const canResume = isPaused || (subscription.cancelAtPeriodEnd && subscription.status !== "CANCELED");

    const getStatusStyles = () => {
        if (isPaused) return { border: "border-blue-200 bg-blue-50/50", icon: "bg-blue-100", iconColor: "text-blue-600", badge: "bg-blue-600" };
        if (isCanceled) return { border: "border-amber-200 bg-amber-50/50", icon: "bg-amber-100", iconColor: "text-amber-600", badge: "bg-amber-600" };
        return { border: "border-green-200 bg-green-50/50", icon: "bg-green-100", iconColor: "text-green-600", badge: "bg-green-600" };
    };

    const styles = getStatusStyles();
    const StatusIcon = isPaused ? PauseCircle : isCanceled ? XCircle : CheckCircle;

    return (
        <Card className={`p-6 border-2 ${styles.border}`}>
            <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.icon}`}>
                    <StatusIcon className={`w-6 h-6 ${styles.iconColor}`} />
                </div>
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{t("billing.supportPlans.subscription")}</h3>
                            <Badge className={`${styles.badge} text-white`}>
                                {isPaused ? t("billing.supportPlans.paused") : isCanceled ? t("billing.supportPlans.canceling") : t("billing.supportPlans.active")}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                            {canResume && (
                                <Button onClick={onResume} disabled={isResuming} size="sm" className="bg-green-600 hover:bg-green-700">
                                    {isResuming ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                                    {t("billing.supportPlans.resume")}
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={onManage} className="text-muted-foreground">
                                {t("billing.supportPlans.manage")}
                            </Button>
                        </div>
                    </div>
                    <p className="text-muted-foreground mb-4">
                        {isPaused ? (
                            <>{t("billing.supportPlans.subscriptionPaused", { defaultValue: "Your subscription is paused. Click Resume to reactivate." })}</>
                        ) : isCanceled ? (
                            <>{t("billing.supportPlans.accessUntil", { date: subscription.currentPeriodEnd ? format(new Date(subscription.currentPeriodEnd), "MMM d, yyyy") : t("billing.supportPlans.endOfPeriod") })}</>
                        ) : (
                            <>{t("billing.supportPlans.hoursRemaining", { remaining: subscription.remainingHours?.toFixed(1), total: subscription.totalHours })}</>
                        )}
                    </p>
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span>
                                {isCanceled ? t("billing.supportPlans.ends") : t("billing.supportPlans.resets")}{" "}
                                {subscription.currentPeriodEnd ? format(new Date(subscription.currentPeriodEnd), "MMM d, yyyy") : t("billing.supportPlans.monthly")}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-muted-foreground" />
                            <span className="capitalize">{subscription.tier?.replace(/_/g, " ").toLowerCase()}</span>
                        </div>
                    </div>
                    {!isCanceled && (
                        <div className="mt-4">
                            <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-muted-foreground">{t("billing.supportPlans.hoursUsedThisMonth")}</span>
                                <span className="font-medium">{usedHours.toFixed(1)} / {subscription.totalHours}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full transition-all ${isPaused ? "bg-blue-500" : "bg-green-500"}`} style={{ width: `${usagePercent}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}
